/* eslint-disable react-hooks/rules-of-hooks */
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "../../../components/ui/chart";
import millify from "millify";

import type { DotProps } from "recharts";
import { useMemo } from "react";
import CustomCursor from "../../../components/charts/chart-cursor";
import { ThreeDWrapper } from "../core/3d-wrapper.core";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import { DateTime } from "luxon";

const CustomActiveDotWithTooltip = (props: DotProps) => {
  const { cx, cy } = props;

  // Ensure valid coordinates for the dot
  if (!cx || !cy) return null;

  return (
    <g>
      <circle
        cursor={"pointer"}
        cx={cx}
        cy={cy}
        r={11}
        fill="rgba(0, 0, 0, 0.5)"
      />
      <circle
        cursor={"pointer"}
        cx={cx}
        cy={cy}
        r={6}
        fill="var(--slider-range)"
      />
    </g>
  );
};

const chartConfig = {
  date: {
    label: "Date",
    color: "black",
  },
  value: {
    label: "Value",
    color: "black",
  },
} satisfies ChartConfig;

const transformData = (
  chartData: Array<{ [date: string]: number }> | undefined,
) => {
  if (!chartData || chartData.length === 0) {
    return [];
  }

  return chartData.map((dataPoint) => {
    const date = Object.keys(dataPoint)[0];
    const count = dataPoint[date];
    return {
      date: date,
      value: count ?? 0,
    };
  });
};

// Function to calculate the maximum value and step size
const calculateMaxValueAndStep = (
  data: Array<{ date: string; value: number }>,
) => {
  if (data.length === 0) {
    return { maxValue: 10, step: 2 };
  }

  let maxValue = Math.max(...data.map((d) => d.value));
  let step: number;

  if (maxValue === 0) {
    maxValue = 10;
    step = 2;
  } else if (maxValue <= 5) {
    step = 1;
    maxValue = 5;
  } else if (maxValue <= 10) {
    step = 2;
    maxValue = 10;
  } else if (maxValue <= 50) {
    step = 10;
    maxValue = 50;
  } else if (maxValue <= 100) {
    step = 20;
    maxValue = 100;
  } else if (maxValue <= 500) {
    step = 100;
    maxValue = 500;
  } else if (maxValue <= 1000) {
    step = 200;
    maxValue = 1000;
  } else {
    step = Math.ceil(maxValue / 5);
    maxValue = Math.ceil(maxValue / step) * step;
  }

  const roundedMaxValue = Math.ceil(maxValue / step) * step;
  return { maxValue: roundedMaxValue, step };
};

// Function to generate ticks
const generateTicks = (min: number, max: number, step: number) => {
  const ticks = [];
  for (let i = min; i <= max; i += step) {
    ticks.push(i);
  }
  return ticks;
};

export function Linechart({
  lineData,
  title,
  dataKey = "posts",
  chartData,
  campaignMetrics,
}: {
  lineData:
    | ICampaignMetricMinimal["postsPerDay"]
    | ICampaignMetricMinimal["totalViewsChart"];
  title: string;
  dataKey?: "posts" | "views" | "likes" | "comments" | "shares";
  chartData: Array<{ category: string; value: number | string }>;
  campaignMetrics: ICampaignMetricMinimal;
}) {
  const transformedData = useMemo(
    () => transformData(lineData ?? []),
    [lineData],
  );
  const { maxValue, step } = useMemo(
    () => calculateMaxValueAndStep(transformedData),
    [transformedData],
  );

  // Update chart config label based on dataKey
  const updatedChartConfig = {
    ...chartConfig,
    value: {
      ...chartConfig.value,
      label: dataKey.charAt(0).toUpperCase() + dataKey.slice(1),
    },
  };

  return (
    <>
      <style>
        {`
          .recharts-cartesian-axis-tick-value {
            fill: black !important;
            font-size: 14px !important;
            font-weight: 500 !important;
          }
        `}
      </style>
      <ThreeDWrapper className="p-0 w-full" hover={false}>
        <Card className="relative bg-transparent border-none rounded-2xl w-full font-cabinetgrotesk text-soloprimary">
          <CardHeader className="flex sm:flex-row items-start border-black border-b">
            <div className="flex flex-col flex-1 justify-center gap-1">
              <CardTitle className="font-medium text-2xl tracking-normal">
                {title}
              </CardTitle>
              <CardDescription className="font-bold text-secondary">
                Last updated{" "}
                {campaignMetrics.lastUpdatedAt
                  ? (() => {
                      const dt = DateTime.fromISO(
                        campaignMetrics.lastUpdatedAt.toString(),
                      );
                      return `${dt.toFormat("h:mm a")} - ${dt.toLocaleString(DateTime.DATE_SHORT)}`;
                    })()
                  : "-"}
              </CardDescription>
            </div>
            <ThreeDWrapper
              hover={false}
              className="flex border border-black rounded-lg overflow-hidden"
            >
              <button
                type="button"
                className="relative flex justify-center items-center px-6 text-left"
              >
                {chartData.map((data, index) => {
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    <div className="flex items-center" key={index}>
                      <div className="flex flex-col text-center">
                        <span className="flex items-center gap-2 font-semibold text-secondary">
                          {data.category}
                        </span>
                        <span className="font-bold text-lg sm:text-3xl leading-none">
                          {data.value}
                        </span>
                      </div>
                      {index < chartData.length - 1 && (
                        <div className="mx-8">
                          <div className="border-r h-10" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </button>
            </ThreeDWrapper>
          </CardHeader>
          <CardContent className="px-2 sm:p-6">
            <ChartContainer
              config={updatedChartConfig}
              className="w-full h-[250px] aspect-auto"
            >
              <LineChart
                accessibilityLayer
                height={450}
                data={transformedData}
                margin={{
                  top: 1,
                  left: 20,
                  right: 12,
                }}
              >
                <CartesianGrid
                  stroke="#2f2f2f50"
                  vertical={false}
                  strokeDasharray="4 4"
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  minTickGap={32}
                  tickFormatter={(value) => {
                    return value.toLocaleString();
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={40}
                  domain={[0, maxValue]}
                  ticks={generateTicks(0, maxValue, step)}
                  tickFormatter={(value) => millify(value)}
                />
                <ChartTooltip
                  cursor={
                    <CustomCursor
                      stroke="black"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      maxValue={maxValue}
                    />
                  }
                  content={() => null}
                />

                <Line
                  dataKey="value"
                  type="monotone"
                  stroke={"black"}
                  strokeWidth={1}
                  dot={
                    <circle
                      cursor={"pointer"}
                      cx={0}
                      cy={0}
                      r={4}
                      fill="var(--slider-range)"
                    />
                  }
                  activeDot={<CustomActiveDotWithTooltip />}
                  cursor={"pointer"}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </ThreeDWrapper>
    </>
  );
}
